interface AuthwallOptions {
	onModalClosed?: () => void;
	redirectOnClose?: string;
	autoStart?: boolean;
}

interface AuthwallReturn {
	isAuthenticated: ComputedRef<boolean>;
	showAuthwall: ComputedRef<boolean>;
	isLoading: Ref<boolean>;
	authenticate: () => void;
	cancel: () => void;
	reset: () => void;
}

/**
 * Composable that implements an authwall - authentication barrier
 * that controls content display based on login status.
 */
export function useAuthwall(options: AuthwallOptions = {}): AuthwallReturn {
	const { loggedIn } = useUserSession();
	const { onModalClosed, redirectOnClose = '/', autoStart = true } = options;
	const { modalState, openLoginModal, closeLoginModal } = useLoginModal();
	const { notifyLogin, notifyLogout } = useAuthCrossTabSync();

	const isLoading = ref(false);
	const isAuthenticated = computed(() => loggedIn.value);
	const showAuthwall = computed(() => modalState.value.isOpen);
	const isInitialized = ref(false);

	function authenticate() {
		if (loggedIn.value) {
			closeLoginModal();
			return;
		}

		openLoginModal();
	}

	function cancel() {
		closeLoginModal();
		if (import.meta.client) {
			if (onModalClosed) {
				onModalClosed();
			} else {
				navigateTo(redirectOnClose);
			}
		}
	}

	function reset() {
		closeLoginModal();
		isLoading.value = false;
	}

	watch(loggedIn, (isLoggedIn, wasLoggedIn) => {
		if (isLoggedIn && !wasLoggedIn) {
			closeLoginModal();
			notifyLogin();
		} else if (!isLoggedIn && wasLoggedIn) {
			notifyLogout();

			if (isInitialized.value && autoStart && !loggedIn.value) {
				authenticate();
			}
		}
	});

	watch(
		() => modalState.value.isOpen,
		(isOpen, wasOpen) => {
			if (wasOpen && !isOpen && !loggedIn.value && isInitialized.value) {
				cancel();
			}
		}
	);

	onMounted(() => {
		isInitialized.value = true;

		if (!loggedIn.value && autoStart) {
			authenticate();
		}
	});

	onUnmounted(() => {
		reset();
	});

	if (import.meta.server && !loggedIn.value && autoStart) {
		openLoginModal();
	}

	return {
		isAuthenticated,
		showAuthwall,
		isLoading: readonly(isLoading),
		authenticate,
		cancel,
		reset
	};
}
